// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — one round of a live race, decoupled from Trystero, the DOM and the clock
// (driven by step()/release()), which is what makes the host-transfer contract testable
// without a network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer holds its OWN orbs — a pure function of the frozen
// seed — and reaches its OWN game-over (all orbs done / a one-life spill in Streak) with
// no host help. NO game state ever crosses the wire — only a tiny status gossip (score +
// orb index as progress, and on finishing, the final score) so the results can show
// everyone's run. HOST TRANSFER IS A PURE DISPLAY CONCERN; onHostChange is wired anyway.

import { createGame, drainEvents, release as releaseGame, step as stepGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  orbIndex: number;
  finished: boolean;
  left: boolean;
}

/** Tiny wire status. Progress rides `s`/`o`; the finish rides `d`. */
export interface RaceMsg {
  s?: number; // score
  o?: number; // orb index (progress)
  d?: boolean; // done
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a
 *  window; further pokes while pending do not push it back. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, { seat, name, score: 0, orbIndex: 0, finished: false, left: false });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  private syncSelf(): void {
    const me = this.self();
    if (!me) return;
    me.score = this.state.score;
    me.orbIndex = this.state.orbIndex;
  }

  /** Advance the local orb by `dtMs` under the current motion energy. Spectators no-op. */
  step(motionEnergy: number, dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    const scoreBefore = this.state.score;
    const orbBefore = this.state.orbIndex;
    stepGame(this.state, motionEnergy, dtMs);
    const events = drainEvents(this.state);
    this.syncSelf();
    if (this.state.score !== scoreBefore || this.state.orbIndex !== orbBefore) {
      this.deps.send({ s: this.state.score, o: this.state.orbIndex });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  /** Release the current orb (bank). Returns queued events. */
  release(): GameEvent[] {
    if (this.selfSeat === null || this.state.over || !this.state.holding) return [];
    releaseGame(this.state);
    const events = drainEvents(this.state);
    this.syncSelf();
    this.deps.send({ s: this.state.score, o: this.state.orbIndex });
    this.deps.onChange();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.finished = true;
      me.score = this.state.score;
      me.orbIndex = this.state.orbIndex;
    }
    this.deps.send({ d: true, s: this.state.score });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return; // never let the wire overwrite our own run
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.s === 'number' && Number.isFinite(msg.s)) r.score = Math.max(0, msg.s | 0);
    if (typeof msg.o === 'number' && Number.isFinite(msg.o)) r.orbIndex = Math.max(0, msg.o | 0);
    if (msg.d) r.finished = true;
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.finished) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.finished && !r.left) return false;
    return true;
  }

  /** Highest score first; unfinished by progress; departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      const rank = (r: PeerResult): number => (r.left ? 2 : r.finished ? 0 : 1);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (b.score !== a.score) return b.score - a.score;
      return a.seat - b.seat;
    });
  }
}
